import React from 'react';
import Paginator from './results/paginator';
import { FormCheck, Placeholder, Table } from 'react-bootstrap-5';
import { ScoreBadge } from './results/score_badges';
import ProductFeature from './product_feature';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { connect, useSelector } from 'react-redux';
import {
  setSearchTableColumnKeys,
  setUpdateResultsOnMapMove
} from 'bundles/connect/components/store/actions/ui';
import { setOffMarketPropertyId } from 'bundles/connect/components/store/actions/off_market_property';
import { EmptyState } from './results/empty_state';
import EmptyStateBadges from './results/empty_state_badges';
import type { RootState } from '../../store/root_reducer';
import { selectEmbeddedMode } from '../../store/selectors/ui';

export const DEFAULT_TABLE_FIELDS = [
  { value: 'owner1Name', label: 'Owner Name' },
  { value: 'likelyToSellRank', label: 'Likely To Sell Rank' },
  { value: 'propertyType', label: 'Property Type' },
  { value: 'lastPurchaseDate', label: 'Last Purchase Date' },
  { value: 'lastPurchasePrice', label: 'Last Purchase Price' },
  { value: 'buildingSize', label: 'Building Size' }
];

export const OTHER_TABLE_FIELDS = [
  { value: 'ownerAddress', label: 'Owner Address' },
  { value: 'ownerCity', label: 'Owner City' },
  { value: 'ownerState', label: 'Owner State' },
  { value: 'ownerZip', label: 'Owner Zip' },
  { value: 'owner2Name', label: 'Other Owner Name' },
  { value: 'ownerOccupied', label: 'Owner Occupied' },
  { value: 'totalBathrooms', label: 'Number of Bathrooms' },
  { value: 'bedrooms', label: 'Number of Bedrooms' },
  { value: 'legalDescription', label: 'Legal Description' },
  { value: 'zoning', label: 'Zoning' },
  { value: 'constructionType', label: 'Construction Type' },
  { value: 'heatingType', label: 'Heating Type' },
  { value: 'heatingFuelType', label: 'Heating Fuel Type' },
  { value: 'fireplaceType', label: 'Fireplace Type' },
  { value: 'garageType', label: 'Garage Type' },
  { value: 'roofCoverType', label: 'Roof Cover Type' },
  { value: 'roofType', label: 'Roof Type' },
  { value: 'lotSize', label: 'Lot Size' },
  { value: 'yearBuilt', label: 'Year Built' },
  { value: 'landAssessedValue', label: 'Land Assessed Value' },
  { value: 'improvementAssessedValue', label: 'Improvement Assessed Value' },
  { value: 'totalAssessedValue', label: 'Total Assessed Value' },
  { value: 'numberOfStories', label: 'Number Of Stories' },
  { value: 'totalRooms', label: 'Total No. Rooms' },
  { value: 'poolType', label: 'Pool Type' },
  { value: 'apn', label: 'APN' },
  { value: 'censusTract', label: 'Census Tract' },
  { value: 'schoolDistrict', label: 'School District' },
  { value: 'owner1Type', label: 'Owner Type' },
  { value: 'owner2Type', label: 'Other Owner Type' },
  { value: 'taxAmount', label: 'Tax Amount' },
  { value: 'taxYear', label: 'Tax Year' },
  { value: 'taxDelinquentYear', label: 'Tax Delinquent Year' },
  { value: 'assessedYear', label: 'Assessed Year' },
  { value: 'schoolTaxDistrict1', label: 'School Tax District 1' },
  { value: 'schoolTaxDistrict2', label: 'School Tax District 2' },
  { value: 'schoolTaxDistrict3', label: 'School Tax District 3' },
  { value: 'basementSize', label: 'Basement Size' },
  { value: 'basementType', label: 'Basement Type' },
  { value: 'garageSize', label: 'Garage Size' },
  { value: 'lotDepth', label: 'Lot Depth' },
  { value: 'lotFrontage', label: 'Lot Frontage' },
  { value: 'fullBathrooms', label: 'Full Bathrooms' },
  { value: 'partialBathrooms', label: 'Partial Bathrooms' },
  { value: 'bathroomFixtures', label: 'Bath Fixtures' },
  { value: 'airConditioning', label: 'AC Type' },
  { value: 'buildingClass', label: 'Building Class' },
  { value: 'buildingCondition', label: 'Building Condition' },
  { value: 'interiorWallType', label: 'Interior Wall Type' },
  { value: 'exteriorWallType', label: 'Exterior Wall Type' },
  { value: 'floorType', label: 'Floor Type' },
  { value: 'locationInfluence', label: 'Location Influence' },
  { value: 'numberOfGarageSpaces', label: 'Number of Garage Spaces' },
  { value: 'drivewayType', label: 'Driveway Type' },
  { value: 'patioType', label: 'Patio Type' },
  { value: 'porchType', label: 'Porch Type' },
  { value: 'residentialUnits', label: '# of Resi Units' },
  { value: 'commercialUnits', label: '# of Commercial Units' },
  { value: 'lotCode', label: 'Lot Code' },
  { value: 'lotNumber', label: 'Lot Number' },
  { value: 'block', label: 'Block' },
  { value: 'district', label: 'District' },
  { value: 'legalUnit', label: 'Legal Unit' },
  { value: 'municipality', label: 'Municipality' },
  { value: 'subdivision', label: 'Subdivision' },
  { value: 'subdivisionPhaseNumber', label: 'Subdivision Phase Number' },
  { value: 'subdivisionTractNumber', label: 'Subdivision Tract Number' },
  { value: 'numberOfOpenLiens', label: 'Total Open Liens' },
  { value: 'amountOfOpenLiens', label: 'Total Open Lien Amount' },
  { value: 'opportunityZone', label: 'Opportunity Zone' },
  { value: 'likelyToRefiRank', label: 'Likely To Refi Rank' },
  { value: 'numberOfBuildings', label: 'Number of Buildings' },
  { value: 'listingSalePrice', label: 'MLS Sale Price' },
  { value: 'listingSoldPrice', label: 'MLS Sold Price' },
  { value: 'originalListingDate', label: 'MLS Listing Date' },
  { value: 'listingSoldDate', label: 'MLS Sold Date' },
  { value: 'listingAgentName', label: 'MLS Agent Name' },
  { value: 'listingAgentPhone', label: 'MLS Agent Phone' },
  { value: 'listingAgentEmail', label: 'MLS Agent Email' },
  { value: 'listingAgentBrokerage', label: 'MLS Agent Brokerage' }
];

type OffMarketResultsTableProps = {
  allResults: RootState['results']['offMarketResults'];
  containerRef: React.RefObject<HTMLDivElement>;
  loading: boolean;
  searchTableFields: Array<{ label: string; value: string }>;
  selectAll: boolean;
  selectedPropertyIds: Set<string>;
  setOffMarketPropertyId: (propertyId: string) => void;
  onSelectAllToggle: () => void;
  onSelectToggle: (propertyId: string) => void;
}

export const ALL_TABLE_FIELDS = [...OTHER_TABLE_FIELDS, ...DEFAULT_TABLE_FIELDS];

const displayField = (key, field) => {
  if (field?.restricted) {
    return <div className=" badge rounded-pill fw-normal text-bg-light">
      <FontAwesomeIcon className="me-1" icon={faLock} />
      <span>Locked</span>
    </div>;
  }

  switch (key) {
  case 'likelyToSellRank':
    return <ScoreBadge label="Sell" score={{ rank: field?.value, ...field }} />;
  case 'likelyToRefiRank':
    return <ScoreBadge label="Refi" score={{ rank: field?.value, ...field }} />;
  case 'ownerOccupied':
    return <div className="text-muted text-nowrap">{field?.value || 'No'}</div>;
  default :
    return <div className="text-muted text-nowrap">{field?.value}</div>;
  }
};

function OffMarketResultsTable(
  {
    allResults,
    containerRef,
    loading,
    searchTableFields,
    selectAll,
    selectedPropertyIds,
    setOffMarketPropertyId,
    onSelectAllToggle,
    onSelectToggle
  }: OffMarketResultsTableProps) {
  const embeddedMode = useSelector(selectEmbeddedMode);

  return (
    <>
      {allResults.length > 0 && (
        <>
          <div className="table-responsive transition-opacity h-100 overflow-auto position-relative bg-white z-2">
            <Table className="my-0 sticky-table" hover>
              <thead className="bg-light">
                <tr className="align-middle sticky-heading">
                  <th className="fw-bolder text-nowrap sticky-cell bg-light">
                    <div className="d-flex gap-2">
                      <ProductFeature product="prospecting">
                        {embeddedMode !== 'buildoutCrm' && (
                          <FormCheck
                            checked={selectAll}
                            data-pendo-tag="results_select_all_checkbox"
                            onChange={onSelectAllToggle}
                          />
                        )}
                      </ProductFeature>
                      <div>Property Address</div>
                    </div>
                  </th>
                  {searchTableFields.map(field => (
                    <th className="fw-bolder text-nowrap" key={field.label}>{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 20 }).map((_, i) => (
                    <tr className="align-middle" key={i}>
                      {Array.from({ length: searchTableFields.length + 1 }).map((_, j) => (
                        <td className="py-3 px-4" key={j} style={{ minWidth: 320 }}>
                          <Placeholder animation="wave">
                            <div>
                              <Placeholder as="div" className="rounded" xs={12} />
                            </div>
                          </Placeholder>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  allResults.map((property => (
                    <tr className="align-middle hover-toggle-parent p-2" key={property.propertyId}>
                      <th className="sticky-cell bg-white" style={{ minWidth: 320 }}>
                        <div className="d-flex gap-3">
                          {embeddedMode !== 'buildoutCrm' && (
                            <FormCheck
                              checked={selectAll || selectedPropertyIds.has(property.propertyId)}
                              onChange={() => onSelectToggle(property.propertyId)}
                            />
                          )}
                          <div>
                            <span
                              className="clickable text-primary fw-normal text-nowrap"
                              onClick={() => setOffMarketPropertyId(property.propertyId)}
                            >
                              {`${property.address}, ${property.city} ${property.state}`}
                            </span>
                          </div>
                        </div>
                      </th>
                      {searchTableFields.map(field => (
                        <td className="bg-white z-1" key={field.value}>
                          {displayField(field.value, property.tableSummary[field.value])}
                        </td>
                      ))}
                    </tr>
                  )))
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-end position-sticky z-3 border-top bottom-0 bg-white p-2">
            <Paginator containerRef={containerRef} />
          </div>
        </>
      )}

      {allResults.length === 0 && (
        <div>
          <EmptyState subtitle="Expand your search to find matching, off market properties" title="No matches" />
          <EmptyStateBadges />
        </div>
      )}
    </>
  );
}

const mapStateToProps = ({
  results: {
    offMarketResults,
  },
  ui: { loading, searchTableColumnKeys, updateResultsOnMapMove }
}) => {
  return {
    allResults: offMarketResults,
    loading,
    searchTableColumnKeys,
    updateResultsOnMapMove
  };
};
export default connect(
  mapStateToProps,
  {
    setOffMarketPropertyId,
    setSearchTableColumnKeys,
    setUpdateResultsOnMapMove
  }
)(OffMarketResultsTable);


